/**
 * useDashboardBootstrap — shared Layout-level data fetched in a single
 * request via `/api/dashboard/bootstrap`.
 *
 * The Layout chrome (branding logo, DST banner, version footer, go-live
 * countdowns, onboarding banners, imported-appointments banner) all read
 * from this shared query so a fresh page load consumes ONE rate-limit
 * slot instead of 7+.
 *
 * Each field is role-gated on the backend and will be `null` for users
 * who should not see it — callers treat `null` as "don't render the
 * corresponding banner/value".
 */
import { useApiGet } from './useApiQuery';
import { useAuthReady } from './useAuthReady';
import { isClientRole } from '../utils/roleConstants';

export interface DashboardBootstrapVersion {
  commit?: string | null;
  deployed_at?: string | null;
  build_timestamp?: string | null;
  demo_mode?: string | null;
  environment?: string | null;
  version?: string | null;
}

export interface DashboardBootstrapBranding {
  logo_url: string | null;
}

export interface DashboardBootstrapDST {
  timezone: string;
  show_reminder: boolean;
  reminder_type: string | null;
  transition: {
    transition_date: string;
    direction: string;
    label: string;
    offset_before: string;
    offset_after: string;
    tz_name: string;
  } | null;
  message: string;
}

export interface DashboardBootstrapFinixOnboarding {
  merchant_status: string;
  onboarding_link_url?: string | null;
  finix_merchant_id?: string | null;
  finix_fee_profile_id?: string | null;
  underwriting_state?: string | null;
  plaid_enabled?: boolean | null;
  merchant_category_code?: string | null;
  banner_dismissed_status?: string | null;
}

export interface DashboardBootstrapGoLiveCountdown {
  show_banner: boolean;
  [key: string]: unknown;
}

export interface DashboardBootstrapImportedAppointments {
  show_banner: boolean;
  imported_count?: number;
  provider_name?: string;
}

export interface DashboardBootstrapInsurance {
  accepts_insurance: boolean;
}

export interface DashboardBootstrap {
  version: DashboardBootstrapVersion;
  branding: DashboardBootstrapBranding | null;
  dst: DashboardBootstrapDST | null;
  finix_onboarding: DashboardBootstrapFinixOnboarding | null;
  go_live_countdown: DashboardBootstrapGoLiveCountdown | null;
  go_live_overview: DashboardBootstrapGoLiveCountdown | null;
  imported_appointments_banner: DashboardBootstrapImportedAppointments | null;
  insurance: DashboardBootstrapInsurance | null;
}

export const DASHBOARD_BOOTSTRAP_QUERY_KEY = ['dashboard', 'bootstrap'] as const;

/**
 * Returns the cached dashboard bootstrap payload.  Multiple callers
 * share a single network request via React Query's single-flight
 * deduplication.  Safe to call from any Layout-level component.
 */
export function useDashboardBootstrap() {
  const { ready, user } = useAuthReady();
  const userRoles = user ? (user.roles || (user.role ? [user.role] : [])) : [];
  const enabled = Boolean(ready && user && !isClientRole(userRoles));
  return useApiGet<DashboardBootstrap>(
    enabled ? DASHBOARD_BOOTSTRAP_QUERY_KEY : null,
    '/api/dashboard/bootstrap',
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}
